import ContractingFooter from "features/contracting/components/ContractingFooter";
import ContractingStepFormProps from "features/contracting/models/ContractingStepFormProps";
import { useContractingStep } from "features/contracting/hooks/useContractingStep";
import PageCard from "features/shared/components/card/PageCard";
import { useForm } from "features/shared/forms/hooks/useForm";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { customerTypeFormValidations } from "./customerTypeFormValidation";
import FormGroup from "features/shared/forms/components/FormGroup";
import { useTranslation } from "react-i18next";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import ContractingNoEditableInfo from "features/contracting/components/ContractingNoEditableInfo";
import { Commodity } from "features/shared/enums/commodity";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { getContractCategory } from "./customerTypeFormHelper";
import { CustomerType } from "features/customer/enums/customerType";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

const CustomerTypeFormSection = (props: ContractingStepFormProps) => {
  // translations
  const tCustomerTypes = useTranslation("customer\\enums\\customerType").t;
  const tCommodities = useTranslation("shared\\enums\\commodity").t;
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "customerTypeFormSection");

  // store
  const stepData = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).progress.steps[props.actualStep.component];

  const commodity = useContractingCustomerTypeValue<Commodity>("commodity");
  const customerType =
    useContractingCustomerTypeValue<CustomerType>("customerType");

  // form hook
  const { values, errors, bindCheckField, valideteForm, setFormValue } =
    useForm({
      initialValues: stepData.values ?? undefined,
      initialValidations: customerTypeFormValidations(),
    });

  // form-step hook
  const { onNextClick, onBackToLoginClick } = useContractingStep({
    ...props,
    formValues: values,
    validateForm: valideteForm,
  });

  // side-effects
  // automatic setting of ContractCategory
  useEffect(() => {
    const category = getContractCategory(customerType, commodity);
    if (category !== null) {
      setFormValue("contractCategoryCode", category);
    }
  }, [customerType, commodity]);

  // render
  return (
    <div>
      <PageCard
        title={tLabels("cardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <FormGroup
          labelText={tLabels("customerType")}
          validationError={errors["customerType"]}
        >
          <div className="row mx-n2">
            <div className="col-lg-4 px-2">
              <RadioBox
                label={tCustomerTypes(CustomerType.Person)}
                icon={{ icon: "home" }}
                disabled={stepData.disabled}
                {...bindCheckField("customerType", CustomerType.Person)}
              />
            </div>
            <div className="col-lg-4 px-2">
              <RadioBox
                label={tCustomerTypes(CustomerType.Company)}
                icon={{ icon: "user-tie" }}
                disabled={stepData.disabled}
                {...bindCheckField("customerType", CustomerType.Company)}
              />
            </div>
          </div>
        </FormGroup>
        <FormGroup
          labelText={tLabels("commodity")}
          validationError={errors["commodity"]}
        >
          <div className="row mx-n2">
            <div className="col-lg-4 px-2">
              <RadioBox
                label={tCommodities(Commodity.Electricity)}
                icon={{ icon: "bolt" }}
                disabled={stepData.disabled}
                {...bindCheckField("commodity", Commodity.Electricity)}
              />
            </div>
            <div className="col-lg-4 px-2">
              <RadioBox
                label={tCommodities(Commodity.Gas)}
                disabled={stepData.disabled}
                icon={{ icon: "fire" }}
                {...bindCheckField("commodity", Commodity.Gas)}
              />
            </div>
          </div>
        </FormGroup>
        <ContractingNoEditableInfo showInfo={stepData.disabled} />
      </PageCard>

      <ContractingFooter
        onBackToLoginClick={
          props.enableBackToLoginBtn ? onBackToLoginClick : undefined
        }
        onNextClick={() => onNextClick(false, true)}
      />
    </div>
  );
};

export default CustomerTypeFormSection;
